import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type SsoProvider } from './ssoEndpoints';

type Status = 'ok' | 'failed';
type ConnectionStatus = 'connected' | 'disconnected';

/**
 * The release a build belongs to: `YYYY.R.B` for a release, `YYYY.Rb.B` for a beta, `dev`
 * for a build that was not packaged. The server reports its own in `/config`; the bundle
 * carries its own in `@constants/appRelease`.
 */
export interface AppRelease {
  version: string;
  commit: string | null;
}

interface ServerStatus {
  status: Status;
  timestamp: string;
  app: AppRelease;
  database: {
    status: ConnectionStatus;
  }
  storage: {
    status: ConnectionStatus;
  }
  // Raw measurements in the base unit each field name carries. The server deliberately
  // formats none of it: units, precision and language are decided where it is rendered.
  process: {
    uptimeSeconds: number;
    responseTimeMs: number;
    memory: {
      rssBytes: number;
      heapTotalBytes: number;
      heapUsedBytes: number;
    }
  }
  system: {
    hostname: string;
    arch: string;
    memory: {
      totalBytes: number;
      freeBytes: number;
    }
    cpu: {
      loadAverage: number[];
      cores: number;
    }
    os: {
      platform: NodeJS.Platform;
      release: string;
      uptimeSeconds: number;
    }
  }
}

export const allowedAuthMethods = ['username_password', 'api_key', 'public'] as const;
export type AllowedMethods = typeof allowedAuthMethods[number];

/**
 * Languages user-authored customization content is written in — POI type names, field
 * labels. Deliberately not called `locales`: this is unrelated to the app's interface
 * language, which comes from the localization CMS.
 */
export interface CustomizationLocales {
  /** Fallback locale. Every label is required to carry it, so nothing renders blank. */
  primary: string;
  enabled: string[];
}

export const defaultCustomizationLocales: CustomizationLocales = { primary: 'en', enabled: ['en'] };

/**
 * What anybody may know of single sign-on before signing in: whether it is on, what its
 * button says, and whether a password still signs anybody but the sysadmin in.
 */
export interface PublicSsoConfig {
  enabled: boolean;
  provider: SsoProvider;
  displayName: string;
  passwordLogin: 'everyone' | 'sysadmin_only';
}

interface Config {
  auth: {
    allowedMethods: AllowedMethods[];
    sessionExpiresInSeconds: number;
  };
  /** Absent on a server from before single sign-on. */
  sso?: PublicSsoConfig;
  api: {
    version: number;
    versionString: string;
  };
  app: AppRelease;
  customizationLocales?: CustomizationLocales;
  devices: {
    serverOnlyMode: boolean;
  };
}

/**
 * What `PUT /config` takes: any of the session settings, the customization languages and the
 * devices setting. A part left out stays as it is stored, and a body with none is refused.
 */
type ConfigUpdate =
  | { auth: Config['auth']; customizationLocales?: CustomizationLocales; devices?: Config['devices'] }
  | { auth?: Config['auth']; customizationLocales: CustomizationLocales; devices?: Config['devices'] }
  | { auth?: Config['auth']; customizationLocales?: CustomizationLocales; devices: Config['devices'] };

export const serverApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Not `/health`: that one is the public liveness probe the container calls, and it
    // answers with status and timestamp only. The detailed report requires `health:get`.
    getStatus: builder.query<ServerStatus, void>({
      query: () => '/health/details',
      onQueryStarted: handleApiResult(),
    }),
    getConfig: builder.query<Config, void>({
      query: () => '/config',
      providesTags: ['Config'],
      onQueryStarted: handleApiResult(),
    }),
    // A caller sends the part it changes and nothing else. The config this tab read can be
    // hours old, so a part sent along only to complete the body would undo a change made
    // elsewhere since.
    updateConfig: builder.mutation<void, ConfigUpdate>({
      query: (config) => ({ url: '/config', method: 'PUT', body: config }),
      invalidatesTags: ['Config'],
      onQueryStarted: handleApiResult('Api_ServerConfig_Update_Successful'),
    }),
  }),
});

export const { useGetStatusQuery, useGetConfigQuery, useUpdateConfigMutation } = serverApiSlice;
